import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Download, ArrowLeft, Zap, Edit, Trash2, Key, Activity, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { EditPilotModal } from '@/components/modals/EditPilotModal';
import { DeleteConfirmDialog } from '@/components/modals/DeleteConfirmDialog';
import { ChangePasswordModal } from '@/components/modals/ChangePasswordModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPilotDashboard({ currentUser, onLogout }) {
  const [pilotData, setPilotData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, byLevel: {} });
  const navigate = useNavigate();
  
  // Modal states
  const [editEntry, setEditEntry] = useState(null);
  const [deleteEntry, setDeleteEntry] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  
  useEffect(() => {
    fetchPilotData();
  }, []);
  
  const fetchPilotData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/pilot`);
      setPilotData(response.data);
      
      const byLevel = {};
      response.data.forEach(item => {
        byLevel[item.level] = (byLevel[item.level] || 0) + 1;
      });
      setStats({ total: response.data.length, byLevel });
    } catch (error) {
      toast.error('Failed to fetch pilot applications');
      console.error('Fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDownloadCSV = async () => {
    try {
      const response = await axios.get(`${API}/pilot/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pilot-applications-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV downloaded successfully');
    } catch (error) {
      toast.error('Failed to download CSV');
      console.error('Download error:', error);
    }
  };
  
  const handleEditSave = async (formData) => {
    try {
      await axios.put(`${API}/pilot/${editEntry.email}`, formData);
      
      // Log audit
      await axios.post(`${API}/admin/audit`, {
        admin_email: currentUser.email,
        admin_name: currentUser.name,
        action: 'edit',
        target: 'pilot',
        entry_id: editEntry.email,
        changes: `Updated pilot application for ${formData.email}`
      });
      
      toast.success('Application updated successfully');
      setEditEntry(null);
      fetchPilotData();
    } catch (error) {
      toast.error('Failed to update application');
      console.error('Update error:', error);
    }
  };
  
  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/pilot/${deleteEntry.email}`);
      
      // Log audit
      await axios.post(`${API}/admin/audit`, {
        admin_email: currentUser.email,
        admin_name: currentUser.name,
        action: 'delete',
        target: 'pilot',
        entry_id: deleteEntry.email,
        changes: `Deleted pilot application for ${deleteEntry.email}`
      });
      
      toast.success('Application deleted successfully');
      setDeleteEntry(null);
      fetchPilotData();
    } catch (error) {
      toast.error('Failed to delete application');
      console.error('Delete error:', error);
    } finally {
      setIsDeleting(false);
    }
  };
  
  const handlePasswordChange = async (formData) => {
    toast.info('Password changes require updating the codebase. Contact system administrator.');
    return false;
  };
  
  const isSuperAdmin = currentUser?.role === 'superadmin';
  
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground font-['Space_Grotesk']">Pilot Applications</h1>
              <Badge className="bg-accent/20 text-accent border-accent/30">
                <Zap className="w-3 h-3 mr-1" />
                High Intent Leads
              </Badge>
              {currentUser && (
                <Badge className={currentUser.role === 'superadmin' ? 'bg-accent/20 text-accent border-accent/30' : 'bg-primary/20 text-primary border-primary/30'}>
                  {currentUser.role === 'superadmin' ? 'Super Admin' : 'Admin'}
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground">
              Clubs interested in pilot programme - priority leads • Viewing as <span className="font-medium text-foreground">{currentUser?.name}</span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {isSuperAdmin && (
              <>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/admin/activity', { state: { currentUser } })}
                  className="border-primary/30"
                >
                  <Activity className="w-4 h-4 mr-2" />
                  Activity Log
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/admin/audit', { state: { currentUser } })}
                  className="border-secondary/30"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Audit Log
                </Button>
              </>
            )}
            <Button 
              variant="outline"
              onClick={() => setShowPasswordModal(true)}
            >
              <Key className="w-4 h-4 mr-2" />
              Change Password
            </Button>
            <Button 
              onClick={handleDownloadCSV}
              className="bg-accent hover:bg-accent/90 text-accent-foreground"
            >
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate('/admin/waitlist')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Waitlist
            </Button>
            <Button 
              variant="outline" 
              onClick={onLogout}
            >
              Logout
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate('/')}
            >
              Home
            </Button>
          </div>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-card border-border/50 p-6">
            <div className="text-sm text-muted-foreground mb-1">Total Applications</div>
            <div className="text-3xl font-bold text-foreground">{stats.total}</div>
          </Card>
          
          {Object.entries(stats.byLevel).slice(0, 3).map(([level, count]) => (
            <Card key={level} className="bg-card border-border/50 p-6">
              <div className="text-sm text-muted-foreground mb-1 capitalize">{level.replace('_', '-')}</div>
              <div className="text-3xl font-bold text-foreground">{count}</div>
            </Card>
          ))}
        </div>
        
        {/* Pilot Applications Table */}
        <Card className="bg-card border-border/50 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border/50">
                  <TableHead className="text-foreground">Club Name</TableHead>
                  <TableHead className="text-foreground">Contact</TableHead>
                  <TableHead className="text-foreground">Email</TableHead>
                  <TableHead className="text-foreground">Role</TableHead>
                  <TableHead className="text-foreground">Level</TableHead>
                  <TableHead className="text-foreground">Teams</TableHead>
                  <TableHead className="text-foreground">Country</TableHead>
                  <TableHead className="text-foreground">Applied</TableHead>
                  {isSuperAdmin && <TableHead className="text-foreground">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={isSuperAdmin ? 9 : 8} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : pilotData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isSuperAdmin ? 9 : 8} className="text-center py-8 text-muted-foreground">
                      No pilot applications yet
                    </TableCell>
                  </TableRow>
                ) : (
                  pilotData.map((item, index) => (
                    <TableRow key={index} className="border-border/50">
                      <TableCell className="font-medium text-foreground">{item.club_name}</TableCell>
                      <TableCell className="text-foreground">{item.contact_name}</TableCell>
                      <TableCell className="font-medium text-foreground">{item.email}</TableCell>
                      <TableCell>
                        <Badge className="bg-primary/10 text-primary border-primary/20 capitalize">
                          {item.role.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-secondary/10 text-secondary border-secondary/20 capitalize">
                          {item.level.replace('_', '-')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-foreground">{item.teams || '-'}</TableCell>
                      <TableCell className="text-foreground">{item.country || '-'}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(item.created_at).toLocaleDateString()}
                      </TableCell>
                      {isSuperAdmin && (
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditEntry(item)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteEntry(item)}
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
      
      {/* Modals */}
      <EditPilotModal
        entry={editEntry}
        open={!!editEntry}
        onClose={() => setEditEntry(null)}
        onSave={handleEditSave}
      />
      
      <DeleteConfirmDialog
        entry={deleteEntry}
        type="pilot"
        open={!!deleteEntry}
        onClose={() => setDeleteEntry(null)}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
      
      <ChangePasswordModal
        currentUser={currentUser}
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSave={handlePasswordChange}
      />
    </div>
  );
}
